import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ComparisonFilter, ComparisonFilterType } from '@/types/compare-offers';
import { KPIComparison } from '@/types/kpi-comparison';
import {
  Filter,
  Package,
  Plus,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';

import { OfferEvolutionInsightsV2 } from '../lib/graphql/generated/graphql';

interface ComparisonFiltersProps {
  evolutionInsights: OfferEvolutionInsightsV2;
  onFilterClick: (filter: ComparisonFilter) => void;
  activeFilter?: ComparisonFilterType;
  onResetClick: () => void;
}

export function ComparisonFilters({
  evolutionInsights,
  onFilterClick,
  activeFilter,
  onResetClick,
}: ComparisonFiltersProps) {
  const { t } = useTranslation();
  const filters: ComparisonFilter[] = [
    {
      type: 'higherDiscounts',
      label: t('components.comparison_filter.higher_discounts'),
      count: evolutionInsights.discountIncreases.length,
      evolutionInsights: evolutionInsights,
      itemIds: evolutionInsights.discountIncreases,
      view: KPIComparison.DISCOUNT_HIGHER,
    },
    {
      type: 'lowerDiscounts',
      label: t('components.comparison_filter.lower_discounts'),
      count: evolutionInsights.discountReductions.length,
      evolutionInsights: evolutionInsights,
      itemIds: evolutionInsights.discountReductions,
      view: KPIComparison.DISCOUNT_LOWER,
    },
    {
      type: 'lowerStock',
      label: t('components.comparison_filter.lower_stock'),
      count: evolutionInsights.stockReductions.length,
      evolutionInsights: evolutionInsights,
      itemIds: evolutionInsights.stockReductions,
      view: KPIComparison.STOCK,
    },
    {
      type: 'newProducts',
      label: t('components.comparison_filter.new_variants'),
      count: evolutionInsights.addedProducts.length,
      evolutionInsights: evolutionInsights,
      itemIds: evolutionInsights.addedProducts,
      view: KPIComparison.SIMPLIFIED,
    },
  ];

  const getFilterIcon = (type: ComparisonFilterType) => {
    switch (type) {
      case 'higherDiscounts':
        return <TrendingUp className="h-4 w-4" />;
      case 'lowerDiscounts':
        return <TrendingDown className="h-4 w-4" />;
      case 'lowerStock':
        return <Package className="h-4 w-4" />;
      case 'newProducts':
        return <Plus className="h-4 w-4" />;
      default:
        return <Filter className="h-4 w-4" />;
    }
  };

  // Filter out empty results
  const availableFilters = filters.filter((filter) => filter.count > 0);

  if (availableFilters.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="p-4">
          <p className="text-center text-sm text-gray-500">
            {t('components.comparison_filter.no_differences_detected')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {activeFilter && (
        <div className="mt-4 flex justify-between rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="">
            <div className="flex items-center gap-2 text-sm text-blue-800">
              <Filter className="h-4 w-4" />
              <span className="font-medium">
                {t('components.comparison_filter.active_filter')}{' '}
                {availableFilters.find((f) => f.type === activeFilter)?.label}
              </span>
            </div>
            <p className="mt-1 text-xs text-blue-600">
              {t('components.comparison_filter.filter_description')}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onResetClick}
            className="text-blue-600 hover:text-blue-800"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="grid gap-3">
        {availableFilters.map((filter) => (
          <Button
            key={filter.type}
            variant="outline"
            className={`flex h-auto items-center justify-between border-2 p-4 transition-all ${
              activeFilter === filter.type
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            } `}
            onClick={() => onFilterClick(filter)}
          >
            <div className="flex items-center gap-3">
              <div
                className={`rounded-full p-2 ${filter.type === 'higherDiscounts' ? 'bg-green-100 text-green-600' : ''} ${filter.type === 'lowerDiscounts' ? 'bg-red-100 text-red-600' : ''} ${filter.type === 'lowerStock' ? 'bg-orange-100 text-orange-600' : ''} ${filter.type === 'newProducts' ? 'bg-blue-100 text-blue-600' : ''} `}
              >
                {getFilterIcon(filter.type)}
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-900">
                  {filter.count} {filter.label}
                </div>
              </div>
            </div>
            <Badge
              variant="secondary"
              className={`text-sm font-medium ${filter.type === 'higherDiscounts' ? 'bg-green-100 text-green-800' : ''} ${filter.type === 'lowerDiscounts' ? 'bg-red-100 text-red-800' : ''} ${filter.type === 'lowerStock' ? 'bg-orange-100 text-orange-800' : ''} ${filter.type === 'newProducts' ? 'bg-blue-100 text-blue-800' : ''} `}
            >
              {filter.count}
            </Badge>
          </Button>
        ))}
      </div>
    </div>
  );
}
